import * as React from 'react';
import { forwardRef, MutableRefObject, ReactElement, useCallback } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
    Box,
    InputAdornment,
    IconButton,
    TextField,
    TextFieldProps,
    alpha,
    styled,
    Theme,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslate } from 'react-admin';

import { SearchInputColor } from './types';

/**
 * A text input built specifically for the search. Very similar to react-admin `SearchInput`,
 * except it does not require to be inside a final-form `Form` component.
 *
 * It accepts all props of the MUI [`<TextField>`](https://mui.com/components/text-fields/).
 * @param {SearchInputProps} props
 * @param {'light' | 'dark'} props.color The color mode for the input, applying light or dark backgrounds
 */
export const SearchInput = forwardRef(function (
    props: SearchInputProps,
    ref: MutableRefObject<HTMLDivElement>
): ReactElement {
    const {
        classes: classesOverride,
        // eslint-disable-next-line
        color = 'light',
        InputProps,
        value,
        disabled,
        variant = 'filled',
        margin = 'dense',
        placeholder = 'ra.action.search',
        ...rest
    } = props;
    const translate = useTranslate();

    const { onFocus, onBlur, onChange } = props;
    const handleClickClearButton = useCallback(
        event => {
            event.preventDefault();
            // Haven't found a better way yet. We also have this issue in react-admin ResetableTextfield
            // @ts-ignore
            onChange('');
        },
        [onChange]
    );

    const handleFocus = useCallback(
        event => {
            onFocus && onFocus(event);
        },
        [onFocus]
    );

    const handleBlur = useCallback(
        event => {
            onBlur && onBlur(event);
        },
        [onBlur]
    );

    const {
        clearButton,
        clearIcon,
        inputAdornmentEnd,
        inputAdornmentStart,
        visibleClearIcon,
        input: inputClassName,
        inputBase: inputBaseClassName,
    } = classes;

    return (
        <Root
            // searchColor is used as a variant for our own styling but is unknown for TextField
            // @ts-ignore
            searchColor={color}
            value={value}
            inputProps={{
                className: inputClassName,
            }}
            InputProps={{
                className: inputBaseClassName,
                disableUnderline: true,
                startAdornment: (
                    <InputAdornment
                        className={inputAdornmentStart}
                        position="start"
                    >
                        <SearchIcon />
                    </InputAdornment>
                ),
                endAdornment: (
                    <InputAdornment
                        className={inputAdornmentEnd}
                        position="end"
                    >
                        {value == '' ? (
                            <Box width={24}> </Box>
                        ) : (
                            <IconButton
                                className={clearButton}
                                aria-label={translate(
                                    'ra.action.clear_input_value'
                                )}
                                title={translate('ra.action.clear_input_value')}
                                disableRipple
                                onClick={handleClickClearButton}
                                onMouseDown={handleMouseDownClearButton}
                                disabled={!value || disabled}
                            >
                                <ClearIcon
                                    className={clsx(
                                        clearIcon,
                                        visibleClearIcon
                                    )}
                                />
                            </IconButton>
                        )}
                    </InputAdornment>
                ),
                ...InputProps,
            }}
            disabled={disabled}
            variant={variant}
            margin={margin}
            placeholder={translate(placeholder, { _: 'Search' })}
            {...rest}
            onFocus={handleFocus}
            onBlur={handleBlur}
            ref={ref}
        />
    );
});

SearchInput.displayName = 'SearchInput';

interface SearchInputProps extends Omit<TextFieldProps, 'color' | 'select'> {
    color?: SearchInputColor;
}

const PREFIX = 'RaSearchInput';

const classes = {
    inputBase: `${PREFIX}-inputBase`,
    input: `${PREFIX}-input`,
    clearIcon: `${PREFIX}-clearIcon`,
    visibleClearIcon: `${PREFIX}-visibleClearIcon`,
    clearButton: `${PREFIX}-clearButton`,
    inputAdornmentStart: `${PREFIX}-inputAdornmentStart`,
    inputAdornmentEnd: `${PREFIX}-inputAdornmentEnd`,
};

const Root = styled(TextField, {
    name: PREFIX,
    overridesResolver: (props, styles) => styles.root,
    shouldForwardProp: prop => prop !== 'searchColor',
})(
    ({
        searchColor,
        theme,
    }: {
        searchColor: SearchInputColor;
        theme: Theme;
    }) => ({
        color:
            searchColor === 'light'
                ? theme.palette.common.white
                : theme.palette.common.black,
        backgroundColor: alpha(
            searchColor === 'light'
                ? theme.palette.common.white
                : theme.palette.common.black,
            0.15
        ),
        marginTop: 4,
        '&:hover': {
            backgroundColor: alpha(
                searchColor === 'light'
                    ? theme.palette.common.white
                    : theme.palette.common.black,
                searchColor === 'light' ? 0.25 : 0.15
            ),
        },
        '&:focus-within': {
            backgroundColor: alpha(
                searchColor === 'light'
                    ? theme.palette.common.white
                    : theme.palette.common.black,
                searchColor === 'light' ? 0.25 : 0.15
            ),
        },
        '&, & *, & .RaSearchInput-inputBase': {
            borderRadius: theme.shape.borderRadius,
        },
        '& *, & .MuiInputBase-root, & .MuiInputAdornment-root': {
            color: 'inherit',
        },
        '& .MuiFilledInput-input': {
            padding: theme.spacing(1, 1, 1, 0),
        },
        '& .MuiFilledInput-underline:before': {
            border: 'none',
        },
        '& .MuiFilledInput-underline:after': {
            border: 'none',
        },
        zIndex: theme.zIndex.modal + 1,
        [`& .${classes.inputBase}`]: {
            background: 'none',
            '&:hover': {
                background: 'none',
            },
        },
        [`& .${classes.input}`]: {
            paddingTop: theme.spacing(1),
            transition: theme.transitions.create('width'),
            width: 120,
        },
        [`& .${classes.clearIcon}`]: {
            height: 16,
            width: 0,
        },
        [`& .${classes.visibleClearIcon}`]: {
            width: 16,
        },
        [`& .${classes.clearButton}`]: {
            color: 'inherit',
            height: 24,
            width: 24,
            padding: 0,
        },
        [`& .Mui-disabled`]: {
            opacity: 0.4,
        },
        [`& .${classes.inputAdornmentStart}`]: {
            marginTop: '0 !important',
        },
        [`& .${classes.inputAdornmentEnd}`]: {
            transition: theme.transitions.create('opacity'),
            opacity: ({ value }: SearchInputProps): number => (!!value ? 1 : 0),
        },
    })
);

const handleMouseDownClearButton = (event: React.MouseEvent): void => {
    event.preventDefault();
};

SearchInput.propTypes = {
    classes: PropTypes.object,
    disabled: PropTypes.bool,
    InputProps: PropTypes.object,
    onBlur: PropTypes.func,
    onChange: PropTypes.func.isRequired,
    onFocus: PropTypes.func,
    value: PropTypes.any,
};
